import {
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type LayoutChangeEvent,
    Pressable,
    Platform,
    Animated,
    View
} from "react-native";
import type IBubbleContentProps from "./type";
import stylesheet, {
    getBubbleMarkdownVariants,
    useStyles
} from "./stylesheet";
import {
    BubbleContentToggleContext
} from "./context";
import {
    BubbleContentToggleIcon
} from "./icons";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useMotionTiming
} from "../../../../helpers/motion";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useBubbleGroupContext,
    withBubbleContext
} from "../../context";
import type {
    BubblePartContentProps
} from "../../type";
import {
    LinearGradient,
    Defs,
    Rect,
    Stop,
    Svg
} from "react-native-svg";
import {
    BUBBLE_MAX_LINES
} from "../../stylesheet";
import MarkdownViewer from "../../../markdownViewer";
import StateLayer from "../../../stateLayer";
import Button from "../../../button";
import BubbleCaret from "../caret";
import Text from "../../../text";

const BubbleContentContent: FC<BubblePartContentProps<IBubbleContentProps>> = ({
    customTheme,
    children,
    context,
    style,
    ...props
}) => {
    const currentTheme = customTheme ?? context.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        activeTheme,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(currentTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(context.customLocalize);

    const groupContext = useBubbleGroupContext();

    const pointerType = usePointerType();

    const id = useId().replace(/[^a-zA-Z0-9]/g, "");

    const [
        contentHeight,
        setContentHeight
    ] = useState<number>(0);

    const [
        isOpen,
        setIsOpen
    ] = useState<boolean>(context.isDefaultExpanded);

    const [
        isAnimating,
        setIsAnimating
    ] = useState<boolean>(false);

    const heightAnim = useRef(new Animated.Value(0)).current;

    const expandMotion = useMotionTiming("expand", currentTheme);

    const collapseMotion = useMotionTiming("collapse", currentTheme);

    const isPressable = !!context.onPress;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled: context.isDisabled
    });

    const maxHeight = context.currentSize.lineHeight * BUBBLE_MAX_LINES;

    const isInverseContent = context.variant === "user" && !isPressable && activeTheme !== "dark";

    const contentTheme = useMemo(() => isInverseContent ? {
        ...currentTheme,
        themeKey: "dark" as const
    } : currentTheme, [
        isInverseContent,
        currentTheme
    ]);

    const isOverflowing = context.isCollapsible && !isPressable && contentHeight > maxHeight;

    const isClipped = isOverflowing && !isOpen;

    const {
        pressable: pressableDynamicStyle,
        surface: surfaceDynamicStyle,
        toggle: toggleDynamicStyle,
        clip: clipDynamicStyle,
        fade: fadeDynamicStyle
    } = useStyles({
        hasReactions: context.reactions.length > 0,
        groupPosition: groupContext?.position,
        currentColors: context.currentColors,
        reactionsSide: context.reactionsSide,
        isClipped: isClipped && !isAnimating,
        currentSize: context.currentSize,
        groupAlign: groupContext?.align,
        variant: context.variant,
        controlPaddings,
        controlHeights,
        isFocusVisible,
        touchTargets,
        shapeConfigs,
        isPressable,
        pointerType,
        maxHeight,
        radiuses,
        borders,
        colors,
        spaces
    });

    const handleLayout = (event: LayoutChangeEvent) => {
        setContentHeight(event.nativeEvent.layout.height);
    };

    const handleToggle = () => {
        const nextMotion = isOpen ? collapseMotion : expandMotion;

        setIsOpen(!isOpen);

        if(!isOverflowing || nextMotion.duration === 0) {
            return;
        }

        heightAnim.stopAnimation();

        heightAnim.setValue(isOpen ? contentHeight : maxHeight);

        setIsAnimating(true);

        Animated.timing(heightAnim, {
            toValue: isOpen ? maxHeight : contentHeight,
            duration: nextMotion.duration,
            easing: nextMotion.easing,
            useNativeDriver: false
        }).start(({
            finished
        }) => {
            if(finished) {
                setIsAnimating(false);
            }
        });
    };

    const toggleContext = useMemo(() => ({
        customTheme: contentTheme,
        isOpen
    }), [
        contentTheme,
        isOpen
    ]);

    const webDirectionProps = Platform.OS === "web" && context.variant === "user" ? {
        dir: "auto"
    } : {};

    const webControlsProps = Platform.OS === "web" ? {
        "aria-controls": id
    } : {};

    const renderText = (text: string) => {
        if(context.contentType === "markdown") {
            return <>
                <MarkdownViewer
                    variants={getBubbleMarkdownVariants({
                        typography: context.currentSize.typography
                    })}
                    customLocalize={context.customLocalize}
                    customTheme={contentTheme}
                    content={text}
                />
                {
                    context.isStreaming ?
                        <BubbleCaret
                            color={context.currentColors.textColor}
                            customTheme={currentTheme}
                        />
                    :
                        null
                }
            </>;
        }

        return <Text
            {...webDirectionProps}
            style={[
                stylesheet.text,
                context.variant === "user" ? stylesheet.user : undefined
            ]}
            customColor={context.currentColors.textColor}
            variant={context.currentSize.typography}
            customTheme={currentTheme}
            selectable={!isPressable}
        >
            {text}
            {
                context.isStreaming ?
                    <BubbleCaret
                        color={context.currentColors.textColor}
                        customTheme={currentTheme}
                    />
                :
                    null
            }
        </Text>;
    };

    const renderBody = () => {
        if(typeof children === "string" || typeof children === "number") {
            return renderText(String(children));
        }

        return children;
    };

    const renderFade = () => {
        if(!isClipped && !isAnimating) {
            return null;
        }

        return <Animated.View
            style={[
                stylesheet.fade,
                fadeDynamicStyle,
                isAnimating ? {
                    opacity: heightAnim.interpolate({
                        inputRange: [
                            maxHeight,
                            Math.max(contentHeight, maxHeight + 1)
                        ],
                        extrapolate: "clamp",
                        outputRange: [
                            1,
                            0
                        ]
                    })
                } : undefined
            ]}
            aria-hidden={true}
        >
            <Svg
                height="100%"
                width="100%"
            >
                <Defs>
                    <LinearGradient
                        id={`${id}fade`}
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                    >
                        <Stop
                            stopColor={context.currentColors.fadeColor}
                            stopOpacity={0}
                            offset="0"
                        />
                        <Stop
                            stopColor={context.currentColors.fadeColor}
                            stopOpacity={1}
                            offset="1"
                        />
                    </LinearGradient>
                </Defs>
                <Rect
                    fill={`url(#${id}fade)`}
                    height="100%"
                    width="100%"
                    x="0"
                    y="0"
                />
            </Svg>
        </Animated.View>;
    };

    const renderToggle = () => {
        if(!isOverflowing) {
            return null;
        }

        return <BubbleContentToggleContext.Provider
            value={toggleContext}
        >
            <Button
                title={localize(isOpen ? "show-less" : "show-more")}
                icon={BubbleContentToggleIcon}
                customTheme={contentTheme}
                style={toggleDynamicStyle}
                onPress={handleToggle}
                aria-expanded={isOpen}
                {...webControlsProps}
                iconDirection="end"
                variant="ghost"
                type="neutral"
                size="small"
            />
        </BubbleContentToggleContext.Provider>;
    };

    const renderSurface = () => {
        return <View
            {...props}
            style={[
                style,
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            {
                isPressable ?
                    <StateLayer
                        borderRadius={context.variant === "plain" ? radiuses.xs : radiuses.chip}
                        customTheme={currentTheme}
                        borderWidth={borders.line}
                        isHovered={isHovered}
                        isPressed={isPressed}
                    />
                :
                    null
            }
            <Animated.View
                style={[
                    stylesheet.clip,
                    clipDynamicStyle,
                    isAnimating ? {
                        height: heightAnim
                    } : undefined
                ]}
                id={id}
            >
                <View
                    style={stylesheet.measure}
                    onLayout={handleLayout}
                >
                    {renderBody()}
                </View>
                {renderFade()}
            </Animated.View>
            {renderToggle()}
        </View>;
    };

    if(!isPressable) {
        return renderSurface();
    }

    return <Pressable
        {...interactionHandlers}
        aria-disabled={context.isDisabled}
        disabled={context.isDisabled}
        style={[
            stylesheet.pressable,
            pressableDynamicStyle
        ]}
        onPress={context.onPress}
        role="button"
    >
        {renderSurface()}
    </Pressable>;
};
export default withBubbleContext("Content", BubbleContentContent);
